import { Refresh as RefreshIcon } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Container, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import React from "react";
import { useLocation } from "react-router-dom";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface FallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

const GENERIC_USER_MESSAGE =
  "We've been notified and are looking into it. Please try again, or reload the page if the problem persists.";

const styles = {
  container: { py: 8 },
  messageText: { mt: 1 },
  errorId: { mt: 1.5, display: "block", color: "text.secondary", fontFamily: "monospace" },
  actions: { display: "flex", gap: 2, justifyContent: "flex-end" }
} as const;

const reloadPage = (): void => {
  window.location.reload();
};

const ErrorFallback: React.FC<FallbackProps> = ({ resetError, eventId }) => {
  return (
    <Container maxWidth="sm" sx={styles.container}>
      <Stack spacing={3} alignItems="stretch">
        <Alert severity="error" variant="outlined">
          <AlertTitle>Something went wrong</AlertTitle>
          <Typography variant="body2" sx={styles.messageText}>
            {GENERIC_USER_MESSAGE}
          </Typography>
          <Typography variant="caption" sx={styles.errorId}>
            Error ID: {eventId}
          </Typography>
        </Alert>
        <Box sx={styles.actions}>
          <Button variant="outlined" onClick={resetError}>
            Try again
          </Button>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={reloadPage}>
            Reload page
          </Button>
        </Box>
      </Stack>
    </Container>
  );
};

const renderFallback = (props: FallbackProps): React.ReactElement => <ErrorFallback {...props} />;

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  return <Sentry.ErrorBoundary fallback={renderFallback}>{children}</Sentry.ErrorBoundary>;
};

export const RouteErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const location = useLocation();

  return (
    <Sentry.ErrorBoundary key={location.pathname} fallback={renderFallback}>
      {children}
    </Sentry.ErrorBoundary>
  );
};
